// Les règles qui relient le relevé bancaire à la grille prévisionnelle.
//
// Un mouvement bancaire ne porte pas de poste : le relevé dit « VIR SEPA
// POINT-P MANOSQUE », pas « Fournitures ». Ces règles font le lien, et une fois
// écrites elles valent pour toutes les années — elles désignent le poste par
// son nom, et les postes se recréent chaque janvier.
//
// Trois choses que cet écran ne fait jamais :
//
//  1. **Enregistrer une règle sans l'avoir montrée à l'œuvre.** Avant d'ajouter
//     ou de modifier, on affiche ce qu'elle prendrait : combien de mouvements,
//     quels montants, et lesquels. Une règle trop large se voit alors tout de
//     suite, et non un mois plus tard dans un total faux.
//  2. **Cacher ce qu'il n'a pas su classer.** Le total non rapproché est
//     affiché en permanence, sorties et entrées séparées.
//  3. **Laisser une règle muette.** Chaque règle dit combien de mouvements elle
//     a réellement pris, et on peut les lire.

import {
  apercuRegle,
  estMotifParDefaut,
  type MouvementAClasser,
  type RegleClassement,
  type SensRegle,
  suggererMotifs,
} from '@imp/shared/classement-mouvements';
import { messageErreur } from '@imp/shared/message-erreur';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import {
  AlertTriangle,
  ArrowDown,
  ArrowUp,
  Check,
  Pencil,
  Plus,
  Trash2,
  Wand2,
  X,
} from 'lucide-react';
import { Fragment, useMemo, useState } from 'react';

import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select } from '@/components/ui/select';
import { formatEuro } from '@/modules/catalogue/api';
import {
  type ChampsRegle,
  createRegleClassement,
  deleteRegleClassement,
  motifsDe,
  parseMontantCellule,
  type RegleStockee,
  type TresoPoste,
  updateRegleClassement,
} from '@/modules/pilotage/tresorerie-api';

/** La règle « toute entrée non reconnue » : son motif n'est fait que d'étoiles. */
function parDefaut(motif: string): boolean {
  const motifs = motifsDe(motif);
  return motifs.length > 0 && motifs.every(estMotifParDefaut);
}

const LIBELLE_SENS: Record<SensRegle, string> = {
  debit: 'Sortie',
  credit: 'Entrée',
};

/** Une règle en cours de saisie — création ou modification, même formulaire. */
interface Brouillon {
  motif: string;
  poste: string;
  sens: SensRegle;
  min: string;
  max: string;
}

const BROUILLON_VIDE: Brouillon = { motif: '', poste: '', sens: 'debit', min: '', max: '' };

function brouillonDe(r: RegleStockee): Brouillon {
  return {
    motif: r.motif,
    poste: r.poste,
    sens: r.sens,
    min: r.montant_min === null ? '' : String(r.montant_min),
    max: r.montant_max === null ? '' : String(r.montant_max),
  };
}

function champsDe(b: Brouillon, ordre: number): ChampsRegle {
  return {
    poste: b.poste,
    motif: b.motif,
    sens: b.sens,
    ordre,
    montant_min: parseMontantCellule(b.min),
    montant_max: parseMontantCellule(b.max),
  };
}

/**
 * Le brouillon sous la forme que le moteur comprend, pour l'aperçu seulement.
 *
 * `posteId` reçoit le NOM du poste : l'aperçu ne regarde que le libellé et le
 * montant, jamais la destination.
 */
function regleDe(b: Brouillon, id: string, ordre: number): RegleClassement {
  return {
    id,
    posteId: b.poste,
    motifs: motifsDe(b.motif),
    sens: b.sens,
    ordre,
    montantMin: parseMontantCellule(b.min),
    montantMax: parseMontantCellule(b.max),
  };
}

function bornesLisibles(min: number | null, max: number | null): string {
  if (min === null && max === null) return '—';
  if (min !== null && max !== null) return `de ${formatEuro(min)} à ${formatEuro(max)}`;
  if (min !== null) return `à partir de ${formatEuro(min)}`;
  return `jusqu’à ${formatEuro(max ?? 0)}`;
}

const APERCU_LIGNES = 8;

/**
 * Ce qu'une règle prendrait, avant de l'enregistrer.
 *
 * Volontairement recalculé à chaque frappe : sur un relevé d'année complète
 * (1 080 mouvements) c'est instantané, et voir le nombre bouger pendant qu'on
 * tape est exactement ce qui empêche d'écrire une règle trop large.
 */
function Apercu({
  brouillon,
  id,
  ordre,
  mouvements,
  reglesActives,
  nouvelle = false,
}: {
  brouillon: Brouillon;
  /** Une règle en création : les étoiles seules (la règle par défaut) y sont refusées. */
  nouvelle?: boolean;
  /** L'identifiant de la règle modifiée, ou une valeur qui n'existe pas (création). */
  id: string;
  ordre: number;
  mouvements: readonly MouvementAClasser[];
  reglesActives: readonly RegleClassement[];
}) {
  const motifs = motifsDe(brouillon.motif);
  if (motifs.length === 0)
    return (
      <p className="text-muted-foreground text-xs">
        Écrivez d’abord ce que le libellé doit contenir : l’aperçu se remplit tout seul.
      </p>
    );

  if (nouvelle && parDefaut(brouillon.motif))
    return (
      <p className="text-xs text-amber-800">
        Les étoiles seules sont réservées à la règle « toute entrée non reconnue », qui
        s’ajoute depuis la grille. Écrivez un fragment du libellé.
      </p>
    );

  const vu = apercuRegle(regleDe(brouillon, id, ordre), mouvements, reglesActives);
  const rien = vu.pris.length === 0;

  return (
    <div
      className={`rounded-md border p-3 ${rien ? 'border-amber-200 bg-amber-50' : 'border-sky-200 bg-sky-50'}`}
    >
      {rien ? (
        <p className="text-sm text-amber-900">
          <strong>Aucun mouvement du relevé ne correspond.</strong> Vérifiez l’orthographe, ou
          élargissez : plusieurs écritures se séparent par une virgule.
        </p>
      ) : (
        <p className="text-sm text-sky-900">
          Cette règle prend <strong>{vu.pris.length}</strong> mouvement
          {vu.pris.length > 1 ? 's' : ''} :{' '}
          <strong className="tabular-nums">{formatEuro(vu.sorties)}</strong> de sorties ·{' '}
          <strong className="tabular-nums">{formatEuro(vu.entrees)}</strong> d’entrées
        </p>
      )}

      {vu.dejaPris.length > 0 ? (
        <p className="mt-1 text-xs text-sky-900">
          {vu.dejaPris.length} autre{vu.dejaPris.length > 1 ? 's' : ''} correspond
          {vu.dejaPris.length > 1 ? 'ent' : ''} aussi, mais {vu.dejaPris.length > 1 ? 'sont' : 'est'}{' '}
          déjà pris par une règle placée avant : rien ne les déplacera.
        </p>
      ) : null}

      {vu.pris.length > 0 ? (
        <table className="mt-2 w-full text-xs">
          <tbody>
            {vu.pris.slice(0, APERCU_LIGNES).map((m) => (
              <tr key={m.id} className="border-t border-sky-200/70">
                <td className="py-1 pr-2 whitespace-nowrap tabular-nums">
                  {m.jour.slice(8, 10)}/{m.jour.slice(5, 7)}
                </td>
                <td className="py-1 pr-2">{m.libelle}</td>
                <td className="py-1 pr-2 text-right tabular-nums text-red-700">
                  {m.debit === 0 ? '' : formatEuro(m.debit)}
                </td>
                <td className="py-1 text-right tabular-nums text-emerald-700">
                  {m.credit === 0 ? '' : formatEuro(m.credit)}
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      ) : null}

      {vu.pris.length > APERCU_LIGNES ? (
        <p className="mt-1 text-xs text-sky-900">
          … et {vu.pris.length - APERCU_LIGNES} autre{vu.pris.length - APERCU_LIGNES > 1 ? 's' : ''}.
        </p>
      ) : null}
    </div>
  );
}

/** Les champs d'une règle, en création comme en modification. */
function ChampsSaisie({
  brouillon,
  setBrouillon,
  postesTries,
  postes,
  prefixe,
  motifFige = false,
}: {
  brouillon: Brouillon;
  setBrouillon: (b: Brouillon) => void;
  /**
   * La règle par défaut : elle ne regarde pas le libellé, et elle ne prend que
   * des entrées. Seuls son poste et ses bornes se modifient.
   */
  motifFige?: boolean;
  postesTries: readonly TresoPoste[];
  postes: readonly TresoPoste[];
  /** Préfixe des identifiants : deux formulaires peuvent être ouverts en même temps. */
  prefixe: string;
}) {
  return (
    <div className="flex flex-wrap items-end gap-3">
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_motif`}>Si le libellé contient</Label>
        <Input
          id={`${prefixe}_motif`}
          className="w-64"
          disabled={motifFige}
          value={motifFige ? 'toute entrée non reconnue' : brouillon.motif}
          placeholder="point p, pointp"
          onChange={(e) => {
            setBrouillon({ ...brouillon, motif: e.target.value });
          }}
        />
        <p className="text-muted-foreground text-[11px]">
          Plusieurs écritures : séparez-les par une virgule.
        </p>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_min`}>Montant de</Label>
        <Input
          id={`${prefixe}_min`}
          className="w-28"
          inputMode="decimal"
          value={brouillon.min}
          placeholder="tout"
          onChange={(e) => {
            setBrouillon({ ...brouillon, min: e.target.value });
          }}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_max`}>à</Label>
        <Input
          id={`${prefixe}_max`}
          className="w-28"
          inputMode="decimal"
          value={brouillon.max}
          placeholder="tout"
          onChange={(e) => {
            setBrouillon({ ...brouillon, max: e.target.value });
          }}
        />
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_poste`}>Ranger dans</Label>
        <Select
          id={`${prefixe}_poste`}
          className="w-56"
          value={brouillon.poste}
          onChange={(e) => {
            const poste = e.target.value;
            const p = postes.find((x) => x.poste === poste);
            setBrouillon({
              ...brouillon,
              poste,
              sens:
                p && !motifFige
                  ? p.categorie === 'encaissements'
                    ? 'credit'
                    : 'debit'
                  : brouillon.sens,
            });
          }}
        >
          <option value="">— choisir —</option>
          {postesTries.map((p) => (
            <option key={p.id} value={p.poste}>
              {p.poste}
            </option>
          ))}
        </Select>
      </div>
      <div className="space-y-1">
        <Label htmlFor={`${prefixe}_sens`}>Sens</Label>
        <Select
          id={`${prefixe}_sens`}
          className="w-32"
          disabled={motifFige}
          value={brouillon.sens}
          onChange={(e) => {
            setBrouillon({ ...brouillon, sens: e.target.value === 'credit' ? 'credit' : 'debit' });
          }}
        >
          <option value="debit">Sortie</option>
          <option value="credit">Entrée</option>
        </Select>
      </div>
    </div>
  );
}

export function RapprochementPanel({
  indisponible,
  regles,
  orphelines,
  postes,
  mouvements,
  nonClasses,
  reglesActives,
  regleParMouvement,
  sortiesNonClassees,
  entreesNonClassees,
}: {
  /**
   * Les règles ou le relevé n'ont pas pu être lus. Alors le panneau ne compte
   * rien : sans règles, tout paraîtrait « sans poste », et « Aucune règle »
   * serait faux.
   */
  indisponible: boolean;
  regles: readonly RegleStockee[];
  /** Règles visant un poste absent de l'année affichée. */
  orphelines: readonly RegleStockee[];
  postes: readonly TresoPoste[];
  /** Tous les mouvements de l'année : l'aperçu les parcourt. */
  mouvements: readonly MouvementAClasser[];
  nonClasses: readonly MouvementAClasser[];
  /** Les règles réellement appliquées, dans leur ordre. */
  reglesActives: readonly RegleClassement[];
  /** Ce que chaque règle a pris, tel que le moteur l'a décidé. */
  regleParMouvement: ReadonlyMap<string, string>;
  /** Jamais additionnés : un total qui mêle entrées et sorties ne veut rien dire. */
  sortiesNonClassees: number;
  entreesNonClassees: number;
}) {
  const qc = useQueryClient();
  const [nouvelle, setNouvelle] = useState<Brouillon>(BROUILLON_VIDE);
  const [edition, setEdition] = useState<{ id: string; brouillon: Brouillon } | null>(null);
  const [ouvert, setOuvert] = useState<string | null>(null);

  const rafraichir = (): void => {
    void qc.invalidateQueries({ queryKey: ['treso-regles'] });
  };

  const ordreSuivant = regles.reduce((max, r) => Math.max(max, r.ordre), 0) + 10;

  const ajouter = useMutation({
    mutationFn: (b: Brouillon) => createRegleClassement(champsDe(b, ordreSuivant)),
    onSuccess: () => {
      setNouvelle(BROUILLON_VIDE);
      rafraichir();
    },
  });

  const modifier = useMutation({
    mutationFn: ({ id, brouillon, ordre }: { id: string; brouillon: Brouillon; ordre: number }) =>
      updateRegleClassement(id, champsDe(brouillon, ordre)),
    onSuccess: () => {
      setEdition(null);
      rafraichir();
    },
  });

  const supprimer = useMutation({
    mutationFn: deleteRegleClassement,
    onSuccess: rafraichir,
  });

  // La règle par défaut passe après toutes les autres, quel que soit son rang :
  // on l'affiche donc en dernier, hors de la série que les flèches renumérotent.
  const triees = useMemo(
    () =>
      [...regles].sort(
        (a, b) =>
          Number(parDefaut(a.motif)) - Number(parDefaut(b.motif)) ||
          a.ordre - b.ordre ||
          a.motif.localeCompare(b.motif, 'fr'),
      ),
    [regles],
  );
  const nbOrdinaires = triees.filter((r) => !parDefaut(r.motif)).length;

  /**
   * Déplace une règle d'un cran, puis renumérote tout par dizaines.
   *
   * Deux règles au même `ordre` se départageraient par un détail, et un
   * déplacement deviendrait imprévisible : on réécrit la série entière.
   */
  const deplacer = useMutation({
    mutationFn: async ({ id, pas }: { id: string; pas: -1 | 1 }) => {
      const liste = triees.filter((r) => !parDefaut(r.motif));
      const i = liste.findIndex((r) => r.id === id);
      const j = i + pas;
      const a = liste[i];
      const b = liste[j];
      if (a === undefined || b === undefined) return;
      liste[i] = b;
      liste[j] = a;
      for (const [k, r] of liste.entries()) {
        const ordre = (k + 1) * 10;
        if (r.ordre !== ordre) await updateRegleClassement(r.id, { ordre });
      }
    },
    onSuccess: rafraichir,
  });

  /** Ce que chaque règle a pris, pour l'afficher ligne par ligne. */
  const prisParRegle = useMemo(() => {
    const par = new Map<string, MouvementAClasser[]>();
    for (const m of mouvements) {
      const id = regleParMouvement.get(m.id);
      if (id === undefined) continue;
      const l = par.get(id);
      if (l) l.push(m);
      else par.set(id, [m]);
    }
    return par;
  }, [mouvements, regleParMouvement]);

  const propositions = useMemo(() => suggererMotifs(nonClasses), [nonClasses]);
  const postesTries = useMemo(
    () => [...postes].sort((a, b) => a.poste.localeCompare(b.poste, 'fr')),
    [postes],
  );

  const complet = (b: Brouillon): boolean => b.poste !== '' && motifsDe(b.motif).length > 0;
  /** En création, les étoiles seules sont refusées : la règle par défaut a son bouton. */
  const completNouvelle = (b: Brouillon): boolean => complet(b) && !parDefaut(b.motif);

  if (indisponible)
    return (
      <div className="border-destructive/40 bg-destructive/10 text-destructive rounded-md border p-3 text-sm">
        <p className="flex items-center gap-2 font-medium">
          <AlertTriangle className="h-4 w-4 shrink-0" />
          Rapprochement indisponible pour le moment.
        </p>
        <p className="mt-1">
          Les règles ou le relevé n’ont pas pu être lus : impossible de dire ce qui est rangé et
          ce qui ne l’est pas. Rien n’est perdu — le message en haut de la page dit ce qui
          s’est passé, et son bouton permet de réessayer.
        </p>
      </div>
    );

  return (
    <div className="space-y-5">
      <p className="text-muted-foreground text-sm">
        Chaque règle dit qu&apos;un libellé de relevé alimente un poste. Elles sont essayées dans
        l&apos;ordre et <strong>la première qui reconnaît le libellé l&apos;emporte</strong> : un
        mouvement n&apos;est jamais compté deux fois. Une règle de sortie retranche les avoirs du
        même fournisseur.
      </p>

      {/* ----- Ce qui n'est pas rapproché ----- */}
      <div
        className={`rounded-md border p-3 ${
          nonClasses.length === 0
            ? 'border-emerald-200 bg-emerald-50'
            : 'border-amber-200 bg-amber-50'
        }`}
      >
        {nonClasses.length === 0 ? (
          <p className="text-sm text-emerald-900">
            Tous les mouvements du relevé sont rattachés à un poste.
          </p>
        ) : (
          <>
            <p className="flex items-center gap-2 text-sm font-medium text-amber-900">
              <AlertTriangle className="h-4 w-4 shrink-0" />
              {nonClasses.length} mouvement{nonClasses.length > 1 ? 's' : ''} sans poste
            </p>
            <p className="mt-1 text-sm text-amber-900">
              <span className="font-semibold">{formatEuro(sortiesNonClassees)}</span> de sorties ·{' '}
              <span className="font-semibold">{formatEuro(entreesNonClassees)}</span> d&apos;entrées
            </p>
            <p className="mt-1 text-xs text-amber-800">
              Ces montants n&apos;apparaissent dans aucune case « réalisé ». Tant qu&apos;ils sont
              là, la comparaison avec le prévisionnel est incomplète. Un chèque émis ou un effet
              domicilié ne porte qu&apos;un numéro : aucune règle ne le reconnaîtra, il se range à la
              main depuis l&apos;onglet « Compte bancaire ».
            </p>
          </>
        )}
      </div>

      {/* ----- Propositions ----- */}
      {propositions.length > 0 ? (
        <div className="space-y-2">
          <p className="flex items-center gap-2 text-sm font-medium">
            <Wand2 className="h-4 w-4" />
            Libellés qui reviennent, et qu&apos;aucune règle ne reconnaît
          </p>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                  <th className="py-1.5 pr-3">Libellé</th>
                  <th className="py-1.5 pr-3 text-right">Fois</th>
                  <th className="py-1.5 pr-3 text-right">Sorties</th>
                  <th className="py-1.5 pr-3 text-right">Entrées</th>
                  <th className="py-1.5">Ranger dans</th>
                </tr>
              </thead>
              <tbody>
                {propositions.map((p) => (
                  <tr key={p.motif} className="border-b last:border-0">
                    <td className="py-1.5 pr-3 font-mono text-xs">{p.motif}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">{p.occurrences}</td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {p.sorties === 0 ? '—' : formatEuro(p.sorties)}
                    </td>
                    <td className="py-1.5 pr-3 text-right tabular-nums">
                      {p.entrees === 0 ? '—' : formatEuro(p.entrees)}
                    </td>
                    <td className="py-1.5">
                      <Select
                        aria-label={`Préparer une règle pour ${p.motif}`}
                        className="h-8 w-56 text-xs"
                        value=""
                        onChange={(e) => {
                          const choisi = e.target.value;
                          if (choisi === '') return;
                          // On remplit le formulaire au lieu d'enregistrer : la
                          // règle se voit à l'œuvre avant d'exister.
                          setNouvelle({
                            motif: p.motif,
                            poste: choisi,
                            sens: p.sens,
                            min: '',
                            max: '',
                          });
                          setEdition(null);
                        }}
                      >
                        <option value="">— choisir un poste —</option>
                        {postesTries.map((x) => (
                          <option key={x.id} value={x.poste}>
                            {x.poste}
                          </option>
                        ))}
                      </Select>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      ) : null}

      {/* ----- Nouvelle règle, avec son aperçu ----- */}
      <div className="space-y-3 rounded-md border p-3">
        <p className="text-sm font-medium">Nouvelle règle</p>
        <ChampsSaisie
          brouillon={nouvelle}
          setBrouillon={setNouvelle}
          postesTries={postesTries}
          postes={postes}
          prefixe="regle_neuve"
        />
        <Apercu
          brouillon={nouvelle}
          id="brouillon-sans-id"
          nouvelle
          ordre={ordreSuivant}
          mouvements={mouvements}
          reglesActives={reglesActives}
        />
        <div className="flex flex-wrap items-center gap-3">
          <Button
            size="sm"
            disabled={!completNouvelle(nouvelle) || ajouter.isPending}
            onClick={() => {
              ajouter.mutate(nouvelle);
            }}
          >
            <Plus className="mr-1.5 h-4 w-4" />
            Enregistrer la règle
          </Button>
          {nouvelle.motif !== '' || nouvelle.poste !== '' ? (
            <Button
              size="sm"
              variant="ghost"
              onClick={() => {
                setNouvelle(BROUILLON_VIDE);
              }}
            >
              Effacer
            </Button>
          ) : null}
          {ajouter.isError ? (
            <span className="text-destructive text-sm">{messageErreur(ajouter.error)}</span>
          ) : null}
        </div>
      </div>

      {/* ----- Les règles existantes ----- */}
      {regles.length === 0 ? (
        <p className="text-muted-foreground text-sm">Aucune règle pour le moment.</p>
      ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[760px] text-sm">
            <thead>
              <tr className="text-muted-foreground border-b text-left text-xs uppercase">
                <th className="w-16 py-1.5 pr-2">Ordre</th>
                <th className="py-1.5 pr-3">Si le libellé contient</th>
                <th className="py-1.5 pr-3">Montant</th>
                <th className="py-1.5 pr-3">Sens</th>
                <th className="py-1.5 pr-3">Poste</th>
                <th className="py-1.5 pr-3 text-right">A pris</th>
                <th className="w-20 py-1.5" />
              </tr>
            </thead>
            <tbody>
              {triees.map((r, i) => {
                const orpheline = orphelines.some((o) => o.id === r.id);
                const pris = prisParRegle.get(r.id) ?? [];
                const sorties = pris.reduce((t, m) => t + Math.max(0, m.debit), 0);
                const entrees = pris.reduce((t, m) => t + Math.max(0, m.credit), 0);
                // Narrowing plutôt qu'un booléen : la ligne modifiée porte son brouillon.
                const edite = edition?.id === r.id ? edition : null;
                const defaut = parDefaut(r.motif);
                return (
                  <Fragment key={r.id}>
                    <tr className="border-b last:border-0">
                      <td className="py-1.5 pr-2">
                        {defaut ? (
                          <span
                            className="text-muted-foreground text-xs"
                            title="Elle passe après toutes les autres règles, quel que soit son rang."
                          >
                            en dernier
                          </span>
                        ) : (
                        <span className="flex items-center gap-0.5">
                          <button
                            type="button"
                            aria-label={`Monter la règle « ${r.motif} »`}
                            disabled={i === 0 || deplacer.isPending}
                            className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-25"
                            onClick={() => {
                              deplacer.mutate({ id: r.id, pas: -1 });
                            }}
                          >
                            <ArrowUp className="h-3.5 w-3.5" />
                          </button>
                          <button
                            type="button"
                            aria-label={`Descendre la règle « ${r.motif} »`}
                            disabled={i >= nbOrdinaires - 1 || deplacer.isPending}
                            className="text-muted-foreground hover:text-foreground rounded p-0.5 disabled:opacity-25"
                            onClick={() => {
                              deplacer.mutate({ id: r.id, pas: 1 });
                            }}
                          >
                            <ArrowDown className="h-3.5 w-3.5" />
                          </button>
                          <span className="text-muted-foreground tabular-nums">{i + 1}</span>
                        </span>
                        )}
                      </td>
                      {defaut ? (
                        <td className="py-1.5 pr-3 text-xs italic">toute entrée non reconnue</td>
                      ) : (
                        <td className="py-1.5 pr-3 font-mono text-xs">{r.motif}</td>
                      )}
                      <td className="text-muted-foreground py-1.5 pr-3 text-xs">
                        {bornesLisibles(r.montant_min, r.montant_max)}
                      </td>
                      <td className="py-1.5 pr-3">{LIBELLE_SENS[r.sens]}</td>
                      <td className="py-1.5 pr-3">
                        {r.poste}
                        {orpheline ? (
                          <span
                            className="ml-2 rounded bg-amber-100 px-1.5 py-0.5 text-xs text-amber-800"
                            title="Ce poste n’existe pas dans la grille de l’année affichée : la règle ne s’applique pas."
                          >
                            poste absent cette année
                          </span>
                        ) : null}
                      </td>
                      <td className="py-1.5 pr-3 text-right">
                        {orpheline ? (
                          <span className="text-muted-foreground">—</span>
                        ) : pris.length === 0 ? (
                          <span
                            className="text-amber-700"
                            title="Aucun mouvement du relevé ne correspond : cette règle ne sert à rien pour le moment."
                          >
                            0
                          </span>
                        ) : (
                          <button
                            type="button"
                            className="text-primary tabular-nums underline-offset-2 hover:underline"
                            onClick={() => {
                              setOuvert(ouvert === r.id ? null : r.id);
                            }}
                          >
                            {pris.length} mvt{pris.length > 1 ? 's' : ''}
                          </button>
                        )}
                      </td>
                      <td className="py-1.5 text-right whitespace-nowrap">
                        <button
                          type="button"
                          aria-label={`Modifier la règle « ${r.motif} »`}
                          className="text-muted-foreground hover:text-foreground rounded p-1"
                          onClick={() => {
                            setEdition(edite !== null ? null : { id: r.id, brouillon: brouillonDe(r) });
                          }}
                        >
                          {edite !== null ? <X className="h-4 w-4" /> : <Pencil className="h-4 w-4" />}
                        </button>
                        <button
                          type="button"
                          aria-label={`Supprimer la règle « ${r.motif} »`}
                          className="text-muted-foreground hover:text-destructive rounded p-1"
                          onClick={() => {
                            supprimer.mutate(r.id);
                          }}
                        >
                          <Trash2 className="h-4 w-4" />
                        </button>
                      </td>
                    </tr>

                    {/* Ce que la règle a réellement pris */}
                    {ouvert === r.id ? (
                      <tr className="bg-muted/40 border-b">
                        <td colSpan={7} className="px-2 py-2">
                          <p className="mb-1 text-xs">
                            <strong className="tabular-nums">{formatEuro(sorties)}</strong> de
                            sorties ·{' '}
                            <strong className="tabular-nums">{formatEuro(entrees)}</strong>{' '}
                            d’entrées — elles alimentent « {r.poste} ».
                          </p>
                          <table className="w-full text-xs">
                            <tbody>
                              {pris.map((m) => (
                                <tr key={m.id} className="border-muted border-t">
                                  <td className="py-1 pr-2 whitespace-nowrap tabular-nums">
                                    {m.jour.slice(8, 10)}/{m.jour.slice(5, 7)}
                                  </td>
                                  <td className="py-1 pr-2">{m.libelle}</td>
                                  <td className="py-1 pr-2 text-right tabular-nums text-red-700">
                                    {m.debit === 0 ? '' : formatEuro(m.debit)}
                                  </td>
                                  <td className="py-1 text-right tabular-nums text-emerald-700">
                                    {m.credit === 0 ? '' : formatEuro(m.credit)}
                                  </td>
                                </tr>
                              ))}
                            </tbody>
                          </table>
                        </td>
                      </tr>
                    ) : null}

                    {/* Modification, avec son aperçu */}
                    {edite !== null ? (
                      <tr className="bg-muted/40 border-b">
                        <td colSpan={7} className="space-y-3 px-2 py-3">
                          <ChampsSaisie
                            brouillon={edite.brouillon}
                            setBrouillon={(brouillon) => {
                              setEdition({ id: r.id, brouillon });
                            }}
                            postesTries={postesTries}
                            postes={postes}
                            prefixe={`regle_${r.id}`}
                            motifFige={defaut}
                          />
                          <Apercu
                            brouillon={edite.brouillon}
                            id={r.id}
                            ordre={r.ordre}
                            mouvements={mouvements}
                            reglesActives={reglesActives}
                          />
                          <div className="flex flex-wrap items-center gap-3">
                            <Button
                              size="sm"
                              disabled={!complet(edite.brouillon) || modifier.isPending}
                              onClick={() => {
                                modifier.mutate({
                                  id: r.id,
                                  brouillon: edite.brouillon,
                                  ordre: r.ordre,
                                });
                              }}
                            >
                              <Check className="mr-1.5 h-4 w-4" />
                              Enregistrer les changements
                            </Button>
                            <Button
                              size="sm"
                              variant="ghost"
                              onClick={() => {
                                setEdition(null);
                              }}
                            >
                              Annuler
                            </Button>
                            {modifier.isError ? (
                              <span className="text-destructive text-sm">
                                {messageErreur(modifier.error)}
                              </span>
                            ) : null}
                          </div>
                        </td>
                      </tr>
                    ) : null}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
          {deplacer.isError ? (
            <p className="text-destructive mt-2 text-sm">{messageErreur(deplacer.error)}</p>
          ) : null}
          {supprimer.isError ? (
            <p className="text-destructive mt-2 text-sm">{messageErreur(supprimer.error)}</p>
          ) : null}
        </div>
      )}
    </div>
  );
}
